/**
 * ShotTimeline Component
 *
 * Visual timeline showing shot segments with cut points.
 * Features:
 * - Horizontal bar representing video duration
 * - Colored segments for each shot
 * - Vertical lines at cut points
 * - Playhead synced with video position
 * - Click-to-seek functionality
 */

import { useRef, useCallback } from 'react';
import type { Shot } from '../api/client';

interface ShotTimelineProps {
  shots: Shot[];
  duration: number;
  currentTime: number;
  onSeek: (time: number) => void;
  color?: 'cyan' | 'purple';
  showLabels?: boolean;
}

export default function ShotTimeline({
  shots,
  duration,
  currentTime,
  onSeek,
  color = 'cyan',
  showLabels = true,
}: ShotTimelineProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Calculate position percentage from time
  const timeToPercent = useCallback(
    (time: number) => {
      if (duration <= 0) return 0;
      return Math.min(100, Math.max(0, (time / duration) * 100));
    },
    [duration]
  );

  // Handle click to seek
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!containerRef.current || duration <= 0) return;

      const rect = containerRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const percent = clickX / rect.width;
      const time = percent * duration;

      onSeek(Math.max(0, Math.min(duration, time)));
    },
    [duration, onSeek]
  );

  // Color classes based on model
  const colorClasses = {
    cyan: {
      segment: 'bg-arena-accent/40',
      segmentHover: 'hover:bg-arena-accent/60',
      cutLine: 'bg-arena-accent',
      playhead: 'bg-arena-accent',
    },
    purple: {
      segment: 'bg-purple-500/40',
      segmentHover: 'hover:bg-purple-500/60',
      cutLine: 'bg-purple-500',
      playhead: 'bg-purple-500',
    },
  };

  const colors = colorClasses[color];

  // No shots = show entire duration as one segment
  if (!shots || shots.length === 0) {
    return (
      <div className="w-full">
        <div
          ref={containerRef}
          onClick={handleClick}
          className="relative h-8 bg-arena-surface rounded-lg overflow-hidden cursor-pointer group"
        >
          {/* Single segment for entire duration */}
          <div className={`absolute inset-0 ${colors.segment}`} />

          {/* Playhead */}
          <div
            className={`absolute top-0 bottom-0 w-0.5 ${colors.playhead} z-10 transition-all duration-100`}
            style={{ left: `${timeToPercent(currentTime)}%` }}
          />

          {/* No shots message */}
          <div className="absolute inset-0 flex items-center justify-center text-arena-muted text-sm">
            No shots detected
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Timeline bar */}
      <div
        ref={containerRef}
        onClick={handleClick}
        className="relative h-8 bg-arena-surface rounded-lg overflow-hidden cursor-pointer group"
      >
        {/* Shot segments */}
        {shots.map((shot, index) => {
          const startPercent = timeToPercent(shot.start_time);
          const endPercent = timeToPercent(shot.end_time);
          const widthPercent = endPercent - startPercent;

          return (
            <div
              key={index}
              className={`absolute top-0 bottom-0 ${colors.segment} ${colors.segmentHover} transition-colors`}
              style={{
                left: `${startPercent}%`,
                width: `${widthPercent}%`,
              }}
              title={`Shot ${index + 1}: ${shot.start_time.toFixed(2)}s - ${shot.end_time.toFixed(2)}s`}
            />
          );
        })}

        {/* Cut point markers (vertical lines between shots) */}
        {shots.slice(1).map((shot, index) => {
          const cutPercent = timeToPercent(shot.start_time);

          return (
            <div
              key={`cut-${index}`}
              className={`absolute top-0 bottom-0 w-0.5 ${colors.cutLine} opacity-80`}
              style={{ left: `${cutPercent}%` }}
            />
          );
        })}

        {/* Playhead indicator */}
        <div
          className={`absolute top-0 bottom-0 w-1 ${colors.playhead} z-10 shadow-lg transition-all duration-75`}
          style={{ left: `calc(${timeToPercent(currentTime)}% - 2px)` }}
        >
          {/* Playhead handle */}
          <div
            className={`absolute -top-1 left-1/2 -translate-x-1/2 w-3 h-3 ${colors.playhead} rounded-full shadow-md`}
          />
        </div>

        {/* Hover effect overlay */}
        <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
      </div>

      {/* Labels */}
      {showLabels && (
        <div className="flex justify-between mt-1 text-xs text-arena-muted">
          <span>0:00</span>
          <span>{formatTime(duration)}</span>
        </div>
      )}
    </div>
  );
}

/**
 * Format seconds to MM:SS
 */
function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}
